import {
    Input as AriaInput,
    FieldError,
    TextField,
} from 'react-aria-components';
import styled, { css, CSSProperties } from 'styled-components';
import { BoxProps, boxStyle } from '../atoms';
import { StyledText, textStyles } from '../atoms/Texts';
import { colors } from '../utils/colors';

export const inputStyle = css<{ $error?: boolean }>`
    ${textStyles({ variant: 'body', weight: 'regular' })}
    ${(props) => {
        return props.theme.input.default;
    }}

   &:disabled {
        ${(props) => props.theme.input.disabled};
    }

    &::placeholder {
        ${(props) => props.theme.input.placeholder};
    }

    &:active {
        ${(props) => props.theme.input.active};
    }

    &:focus-visible {
        ${(props) => props.theme.input.focus};
    }

    &:hover {
        ${(props) => props.theme.input.hover};
    }

    ${(props) =>
        props.$error &&
        css`
            ${(props) => props.theme.input.error}

            &:hover {
                ${(props) => props.theme.input.error.hover}
            }
        `}
`;

export const InputStyle = styled(AriaInput)<{ $error?: boolean }>`
    ${inputStyle}
`;

export type InputStyles = {
    default: CSSProperties;
    placeholder: CSSProperties;
    hover: CSSProperties;
    focus: CSSProperties;
    active: CSSProperties;
    disabled: CSSProperties;
    error: CSSProperties & { hover?: CSSProperties };
};

export const defaultInputStyles: InputStyles = {
    default: {
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        outline: `0.125rem solid ${colors.black}`,
        border: 'none',
        transition: 'all 0.2s',
    },
    placeholder: {
        color: colors.gray500,
    },
    hover: {
        outline: `0.125rem solid ${colors.blue600}`,
    },
    focus: {
        outline: `0.25rem solid ${colors.indigo400}`,
    },
    active: {
        outline: `0.1875rem solid ${colors.blue600}`,
    },
    disabled: {
        background: colors.gray100,
        outlineColor: colors.gray400,
        color: colors.gray400,
        cursor: 'not-allowed',
    },
    error: {
        outlineColor: colors.red700,
        color: colors.red700,
        hover: {
            background: colors.red50,
            outline: `0.125rem solid ${colors.red700}`,
        },
    },
};

interface StyledInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    validate: (value: string) => string | null;
}

export const Input: React.FC<StyledInputProps> = ({
    label,
    validate,
    ...rest
}: StyledInputProps) => {
    return (
        <TextFieldContainer direction="column" gap="8" validate={validate}>
            <StyledText variant="label" weight="bold">
                {label}
            </StyledText>
            <InputStyle {...rest} />
            <ErrorField />
        </TextFieldContainer>
    );
};

const TextFieldContainer = styled(TextField)<BoxProps>`
    ${boxStyle}
`;

const ErrorField = styled(FieldError)`
    ${(props) => {
        return props.theme.texts.error.default;
    }}
`;
